import type {
  AlertEnvironment,
  AlertEvidence,
  AlertKind,
  AlertPayload,
  AlertSeverity,
  AlertSource,
  AlertStatus,
  CheckerMode,
  ComplianceAlertDetail,
  ComplianceAlertEvent,
  ComplianceAlertRow,
  ComplianceAlertsPage,
  ComplianceAlertsParams,
  ComplianceCreateAlertRequest,
  ComplianceOverview,
  ComplianceSurface,
  ComplianceUserRisk,
  ComplianceUserRow,
  ComplianceUsersPage,
  ComplianceUsersParams,
  RiskTimelineEvent,
  RuleStats,
  ScoringPolicy,
  SignalId,
  SurfaceDailyPoint,
  SurfaceStats,
  SurfaceWindowStats,
  UserSignalStatus,
  ComplianceOverviewParams,
  OverviewWindow,
  PendingRequest,
  RiskCriteria,
  TraderSpend,
} from '@core/api/complianceTypes';
import {
  CHECKER_RULES, COMPLIANCE_SURFACES, PHASE2_SIGNALS, RECAP_SURFACES, SCORE_EXEMPT_SIGNALS, kindGroup, requiresSupervisorToResolve,
} from '@core/api/complianceRules';
import { mockUsers } from './mockData';

/**
 * Deterministic mock of the Compliance console (`/api/v1/admin/compliance/*`). Seeded like the rest of
 * the mock layer so the demo never flickers, and content-blind like the contract: every string it
 * emits is an id, a date, a kind, a rule id, a hash, a version or a template sentence built from those.
 * Status changes made in the session live in module state, exactly as the other mock mutations do.
 */

// ─── Seeded PRNG (the same mulberry32 the dataset uses) ──────────────────────────────────────
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

const HEX = '0123456789abcdef';
function fakeSha256(seed: string): string {
  const rng = mulberry32(hashString(seed));
  let out = '';
  for (let i = 0; i < 64; i++) out += HEX[Math.floor(rng() * 16)];
  return out;
}

function uuidLike(seed: string): string {
  const h = fakeSha256(seed);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-a${h.slice(17, 20)}-${h.slice(20, 32)}`;
}

const DAY = 86400_000;
const HOUR = 3600_000;
/** One anchor for the whole mock so "now" is the same in every function of a session. */
const NOW = Date.now();
const iso = (ms: number) => new Date(ms).toISOString();
const ymd = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export const SCORING: ScoringPolicy = { p1: 10, p2: 5, p3: 1, halfLifeDays: 14, windowDays: 30, reviewScore: 10 };

const POINTS: Record<AlertSeverity, number> = { p1: SCORING.p1, p2: SCORING.p2, p3: SCORING.p3 };

const SIGNAL_SEVERITY: Record<SignalId, AlertSeverity> = {
  S1: 'p2', S2: 'p2', S3: 'p2', S4: 'p3', S5: 'p2', S6: 'p3', S7: 'p2', S8: 'p1', S9: 'p1', S10: 'p2',
  S11: 'p3', S12: 'p3', S13: 'p3', S14: 'p2', S15: 'p3', S16: 'p3', S17: 'p1', S18: 'p3', S19: 'p3',
  S20: 'p1', S21: 'p3',
};

const ALL_SIGNALS: readonly SignalId[] = [
  'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'S9', 'S10', 'S11',
  'S12', 'S13', 'S14', 'S15', 'S16', 'S17', 'S18', 'S19', 'S20', 'S21',
];

/** The fixed titles the backend's derived-alert summary carries (`RiskSignalCatalog.Titles`), phase-2 signals only. */
const SIGNAL_TITLE: Partial<Record<SignalId, string>> = {
  S1: 'repeated blocks', S2: 'replies flagged as advice or a forecast', S3: 'replies flagged for breaking the assistant\'s rules',
  S8: 'AI use without a current disclaimer acceptance', S9: 'rows on a dark surface',
  S10: 'AI use by an account outside the firm', S11: 'spend and credit anomalies',
  S15: 'withheld or flagged recaps', S17: 'the same user across signals', S20: 'provenance gaps',
};

/**
 * The backend always sends all 21 signals, S1 to S21 in order: the ten the phase-2 evaluator lights read
 * `lit` or `clear`, the other eleven `not-recorded` (with no facts: nothing was counted). The mock does the same.
 */
function fullSignals(evaluated: readonly UserSignalStatus[]): UserSignalStatus[] {
  return ALL_SIGNALS.map(id => evaluated.find(s => s.id === id)
    ?? { id, severity: SIGNAL_SEVERITY[id], state: 'not-recorded' as const, count: 0, points: 0, firstAt: null, lastAt: null, facts: {} });
}

/** The disclaimer version in force in the mock (the overview's checks and the per-user view agree on it). */
const MOCK_DISCLAIMER_VERSION = '2026-09-10.1';

/** The kind of the record "Mark reviewed" writes: a manual p3 alert about the user, resolved at birth. */
export const USER_REVIEWED_KIND = 'user-reviewed';

/** The mock's median trader-day and the S11 spend line it makes (3 x the median), as the backend computes them. */
const MEDIAN_DAY_USD = 0.6;
const SPEND_LINE_USD = MEDIAN_DAY_USD * 3;

/**
 * The lines in force in the mock (`criteria`): the backend's D6 starting values (`RiskThresholdsOptions`), and a spend
 * line of 3 x a fixed median trader-day. The mock's signals light by these same lines, so an explanation never shows a
 * count under its line on a signal marked over it.
 */
export const MOCK_CRITERIA: RiskCriteria = {
  windowDays: SCORING.windowDays,
  s1: { windowDays: 7, finalBlocks: 2, firstAttemptBlocks: 3, p1Blocks: 5 },
  s2: { hits: 3 },
  s3: { p2Hits: 1, p1Hits: 2 },
  s11: {
    medianMultiplier: 3, dailySpendUsd: null, medianDayUsd: money(MEDIAN_DAY_USD), spendLineUsd: money(SPEND_LINE_USD),
    absorbedCallsPerDay: 10, adminGrants: 3,
  },
  s17: { signals: 2 },
};

/** Money as the backend writes it: a decimal string with no trailing zeros ("0.6", "12.5", "3"). */
function money(n: number): string {
  return String(Number(n.toFixed(4)));
}

// ─── Users at risk ─────────────────────────────────────────────────────────────────────────

interface RiskUser {
  userId: string;
  email: string;
  firmOrTestAccount: boolean;
  signals: UserSignalStatus[];
  timeline: RiskTimelineEvent[];
  acceptances: { version: string; acceptedAt: string }[];
  lastAiActivityAt: string | null;
  score: number;
  spend: TraderSpend;
}

const SURFACE_FOR_TURN: ComplianceSurface[] = ['day-assist-plan', 'day-assist-review'];
const RULE_FOR: Record<'S2' | 'S3', string[]> = {
  S2: ['advice-prescription', 'market-prediction'],
  S3: ['control-override-claim', 'system-prompt-echo', 'format-envelope-breach', 'advisor-persona', 'broker-solicitation'],
};

function pointsFor(severity: AlertSeverity, ageDays: number): number {
  const base = POINTS[severity];
  return ageDays > SCORING.halfLifeDays ? base / 2 : base;
}

/** A time within the last `days` UTC days (today included), seeded. */
function within(rng: () => number, days: number): number {
  return NOW - Math.floor(rng() * days) * DAY - Math.floor(rng() * Math.min(DAY, NOW % DAY || DAY));
}

function buildRiskUser(userId: string, email: string): RiskUser | null {
  const rng = mulberry32(hashString(userId + 'risk'));
  // About a fifth of the directory has any AI signal at all; the rest never appear on the ranked list.
  if (rng() > 0.2) return null;
  const firmOrTestAccount = rng() < 0.3;
  const timeline: RiskTimelineEvent[] = [];
  const signals: UserSignalStatus[] = [];
  let score = 0;
  let lastAi = 0;
  /** The trader's model calls by UTC day: what S11 reads. */
  const usage = new Map<string, { cost: number; calls: number; uncharged: number }>();

  const push = (e: Omit<RiskTimelineEvent, 'alertId'> & { alertId?: string | null }) => {
    timeline.push({ alertId: null, ...e });
    lastAi = Math.max(lastAi, Date.parse(e.at));
  };
  const recordUsage = (at: number, cost: number, calls: number, uncharged: number) => {
    const day = ymd(at);
    const d = usage.get(day) ?? { cost: 0, calls: 0, uncharged: 0 };
    usage.set(day, { cost: d.cost + cost, calls: d.calls + calls, uncharged: d.uncharged + uncharged });
    lastAi = Math.max(lastAi, at);
  };

  /**
   * One signal as the evaluator reads it: `count` occurrences in its window, lit when `lit(count)` says so (by the lines
   * of `MOCK_CRITERIA`), scored only when lit and never for the score-exempt signals. Returns the status so the caller
   * can set its facts.
   */
  const decide = (
    id: SignalId, count: number, mk: (i: number, at: number) => AlertSeverity | void,
    { windowDays = SCORING.windowDays, lit = (n: number) => n > 0, severity = () => SIGNAL_SEVERITY[id] }:
    { windowDays?: number; lit?: (n: number) => boolean; severity?: (n: number) => AlertSeverity } = {},
  ): UserSignalStatus => {
    const isLit = lit(count);
    const settled = severity(count);
    let first: number | null = null;
    let last: number | null = null;
    let pts = 0;
    for (let i = 0; i < count; i++) {
      const at = within(rng, windowDays);
      first = first === null ? at : Math.min(first, at);
      last = last === null ? at : Math.max(last, at);
      const own = mk(i, at) ?? settled;
      // The backend's ScoreExempt set (the SPA's SCORE_EXEMPT_SIGNALS): the firm's controls and a data right never score.
      if (isLit && !SCORE_EXEMPT_SIGNALS.includes(id)) pts += pointsFor(own, (NOW - at) / DAY);
    }
    score += pts;
    const status: UserSignalStatus = {
      id, severity: isLit ? settled : SIGNAL_SEVERITY[id], state: isLit ? 'lit' : 'clear', count, points: pts,
      firstAt: first === null ? null : iso(first), lastAt: last === null ? null : iso(last), facts: {},
    };
    signals.push(status);
    return status;
  };

  const c = MOCK_CRITERIA;
  // S1 held-back answers in the last 7 days: 0..4 requests, each first answer held back; two in three rewrites held back too.
  let finals = 0;
  const s1 = decide('S1', rng() < 0.55 ? Math.floor(rng() * 5) : 0, (i, at) => {
    const surface = SURFACE_FOR_TURN[i % 2]!;
    push({ at: iso(at - 40_000), type: 'first-attempt-block', signalId: 'S1', source: 'assist_turns', rowId: uuidLike(`${userId}-fa-${i}`), surface, ruleId: null, ruleSeverity: null, checkerMode: 'enforce', amountUsd: null, label: null });
    if (i % 3 !== 2) {
      finals++;
      push({ at: iso(at), type: 'final-block', signalId: 'S1', source: 'assist_turns', rowId: uuidLike(`${userId}-fb-${i}`), surface, ruleId: null, ruleSeverity: null, checkerMode: 'enforce', amountUsd: null, label: null });
    }
  }, {
    windowDays: c.s1.windowDays,
    lit: n => n >= c.s1.firstAttemptBlocks || Math.ceil((n * 2) / 3) >= c.s1.finalBlocks,
    severity: n => (n >= c.s1.p1Blocks ? 'p1' : 'p2'),
  });
  s1.facts = { firstAttemptBlocks: s1.count, finalBlocks: finals };
  // S2 advice and forecast flags: 0..4, over the line at 3.
  const s2 = decide('S2', rng() < 0.6 ? Math.floor(rng() * 5) : 0, (i, at) => {
    push({ at: iso(at), type: 'flag', signalId: 'S2', source: 'review_flags', rowId: uuidLike(`${userId}-s2-${i}`), surface: SURFACE_FOR_TURN[i % 2]!, ruleId: RULE_FOR.S2[i % 2]!, ruleSeverity: 1, checkerMode: 'enforce', amountUsd: null, label: null });
  }, { lit: n => n >= c.s2.hits });
  s2.facts = { hits: s2.count, rows: s2.count };
  // S3 rule-break flags: rare; one is over the line, two make it due today.
  const s3 = decide('S3', rng() < 0.15 ? 1 + Math.floor(rng() * 2) : 0, (i, at) => {
    push({ at: iso(at), type: 'flag', signalId: 'S3', source: 'review_flags', rowId: uuidLike(`${userId}-s3-${i}`), surface: 'day-assist-plan', ruleId: RULE_FOR.S3[i % RULE_FOR.S3.length]!, ruleSeverity: 1, checkerMode: 'enforce', amountUsd: null, label: null });
  }, { lit: n => n >= c.s3.p2Hits, severity: n => (n >= c.s3.p1Hits ? 'p1' : 'p2') });
  s3.facts = { hits: s3.count, rows: s3.count };
  // S8 AI use with no current acceptance: very rare (a control failure).
  const s8 = decide('S8', rng() < 0.05 ? 1 : 0, (i, at) => {
    push({ at: iso(at), type: 'dark-surface-row', signalId: 'S8', source: 'outputs', rowId: uuidLike(`${userId}-s8-${i}`), surface: 'day-write-up', ruleId: null, ruleSeverity: null, checkerMode: null, amountUsd: null, label: 'no-current-acceptance' });
  });
  s8.facts = { rows: s8.count };
  // S9 a row on a dark surface: none in the mock's dev environment (the honest common case).
  decide('S9', 0, () => {}).facts = { rows: 0 };
  // S10 real people on dev AI: every non-firm account with AI rows (its facts are set once the usage is known).
  const s10 = decide('S10', firmOrTestAccount ? 0 : 1, (_i, at) => {
    push({ at: iso(at), type: 'flag', signalId: 'S10', source: 'users', rowId: userId, surface: null, ruleId: null, ruleSeverity: null, checkerMode: null, amountUsd: null, label: 'domain-not-in-allow-list' });
  });
  // S11 spend and credits, as the evaluator counts it: a day over the spend line (P3), a day with 10+ calls not charged
  // (P2), or 3+ admin grants in the window (one P3 occurrence). Up to three kinds, in that order.
  let spendDays = 0;
  let absorbedDays = 0;
  let grants = 0;
  const s11 = decide('S11', rng() < 0.35 ? 1 + Math.floor(rng() * 3) : 0, (i, at) => {
    const kind = i % 3;
    if (kind === 0) {
      spendDays++;
      const cost = SPEND_LINE_USD * (1.5 + rng() * 3.5);
      recordUsage(at, cost, 1, 0);
      push({ at: iso(at), type: 'spend', signalId: 'S11', source: 'prompt_usage', rowId: uuidLike(`${userId}-sp-${i}`), surface: 'backtest-chat', ruleId: null, ruleSeverity: null, checkerMode: null, amountUsd: money(cost), label: null });
      return 'p3';
    }
    if (kind === 1) {
      absorbedDays++;
      const calls = c.s11.absorbedCallsPerDay + Math.floor(rng() * 4);
      recordUsage(at, 0, calls, calls);
      for (let k = 0; k < calls; k++) {
        push({ at: iso(at - k * 60_000), type: 'uncharged-call', signalId: 'S11', source: 'prompt_usage', rowId: uuidLike(`${userId}-uc-${i}-${k}`), surface: 'day-assist-plan', ruleId: null, ruleSeverity: null, checkerMode: null, amountUsd: null, label: k % 2 ? 'duplicate_request' : 'blocked' });
      }
      return 'p2';
    }
    for (let k = 0; k < c.s11.adminGrants; k++) {
      grants++;
      push({ at: iso(Math.max(at - k * 2 * DAY, NOW - (SCORING.windowDays - 1) * DAY)), type: 'admin-grant', signalId: 'S11', source: 'credit_ledger', rowId: uuidLike(`${userId}-ag-${i}-${k}`), surface: null, ruleId: null, ruleSeverity: null, checkerMode: null, amountUsd: null, label: 'AdminGrant' });
    }
    return 'p3';
  }, { severity: () => 'p3' });
  if (absorbedDays > 0) s11.severity = 'p2';
  s11.facts = { spendDays, absorbedCallDays: absorbedDays, adminGrants: grants };
  // S15 withheld recaps.
  const s15 = decide('S15', rng() < 0.3 ? 1 + Math.floor(rng() * 2) : 0, (i, at) => {
    push({ at: iso(at), type: 'withheld-recap', signalId: 'S15', source: 'outputs', rowId: uuidLike(`${userId}-wr-${i}`), surface: RECAP_SURFACES[i % RECAP_SURFACES.length]!, ruleId: null, ruleSeverity: null, checkerMode: 'enforce', amountUsd: null, label: null });
  });
  s15.facts = { withheld: s15.count, emailedFlagged: 0 };
  // S17 composite: any two of S1 to S3 lit, one P1 occurrence; its count is how many of the three are present.
  const core = [s1, s2, s3].filter(s => s.state === 'lit');
  const s17 = decide('S17', core.length >= c.s17.signals ? 1 : 0, () => {}, { lit: n => n > 0 });
  s17.count = core.length;
  s17.facts = { signals: core.length, ...Object.fromEntries(core.map(s => [`with${s.id}`, 1])) };
  // S20 provenance gaps: not a user matter and none in the mock.
  decide('S20', 0, () => {}).facts = { gaps: 0 };

  // Ordinary use on other days, under the spend line (the typical trader-day is the median), so the day list reads true.
  for (let d = 0; d < SCORING.windowDays; d++) {
    const at = NOW - d * DAY - Math.floor(rng() * 6) * HOUR;
    if (usage.has(ymd(at)) || rng() > 0.45) continue;
    recordUsage(at, MEDIAN_DAY_USD * (0.2 + rng() * 1.8), 1 + Math.floor(rng() * 6), 0);
  }
  const days = [...usage.entries()].sort(([a], [b]) => a.localeCompare(b));
  const totalCalls = days.reduce((n, [, d]) => n + d.calls, 0);
  s10.facts = { aiRows: totalCalls };
  const spend: TraderSpend = {
    windowCostUsd: money(days.reduce((n, [, d]) => n + d.cost, 0)),
    calls: totalCalls,
    unchargedCalls: days.reduce((n, [, d]) => n + d.uncharged, 0),
    days: days.map(([day, d]) => ({
      day, costUsd: money(d.cost), calls: d.calls, unchargedCalls: d.uncharged,
      overSpendLine: d.cost > SPEND_LINE_USD, overUnchargedLine: d.uncharged >= c.s11.absorbedCallsPerDay,
    })),
  };

  // Ordinary activity so the timeline is not only trouble: stops, resets, acceptances.
  const stops = Math.floor(rng() * 4);
  for (let i = 0; i < stops; i++) {
    const at = NOW - Math.floor(rng() * 30) * DAY;
    push({ at: iso(at), type: i % 2 ? 'stop' : 'reset', signalId: null, source: 'assist_turns', rowId: uuidLike(`${userId}-st-${i}`), surface: 'day-assist-plan', ruleId: null, ruleSeverity: null, checkerMode: null, amountUsd: null, label: null });
  }
  const acceptedAt = NOW - (40 + Math.floor(rng() * 200)) * DAY;
  const acceptances = [{ version: '2026-09-10.1', acceptedAt: iso(acceptedAt) }];
  push({ at: iso(acceptedAt), type: 'acceptance', signalId: null, source: 'disclaimer_acceptances', rowId: uuidLike(`${userId}-acc`), surface: null, ruleId: null, ruleSeverity: null, checkerMode: null, amountUsd: null, label: '2026-09-10.1' });

  timeline.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  // Only a trader with a lit signal is on the ranked list (an open alert is the other way on, and the mock's alerts
  // about a trader come from lit signals).
  if (!signals.some(s => s.state === 'lit')) return null;
  return {
    userId, email, firmOrTestAccount, signals: fullSignals(signals), timeline, acceptances,
    lastAiActivityAt: lastAi ? iso(lastAi) : null,
    score: Math.round(score * 10) / 10,
    spend,
  };
}

let _riskUsers: RiskUser[] | null = null;
function riskUsers(): RiskUser[] {
  if (!_riskUsers) {
    _riskUsers = mockUsers()
      .map(u => buildRiskUser(u.userId, u.email))
      .filter((u): u is RiskUser => u !== null)
      .sort((a, b) => b.score - a.score || a.userId.localeCompare(b.userId));
  }
  return _riskUsers;
}

// ─── Alerts ────────────────────────────────────────────────────────────────────────────────

interface SeedAlert {
  row: ComplianceAlertRow;
  fingerprint: string | null;
  snsMessageId: string | null;
  payload: AlertPayload;
  raw: ComplianceAlertDetail['raw'];
  evidence: AlertEvidence[];
  seedEvents: ComplianceAlertEvent[];
  createdBy: string;
}

interface AlertTemplate {
  kind: AlertKind;
  severity: AlertSeverity;
  source: AlertSource;
  environment: AlertEnvironment;
  summary: (n: number) => string;
  payload: (n: number) => AlertPayload;
  evidence: (n: number) => AlertEvidence[];
}

const LOG_GROUP: Record<'dev' | 'prod', string> = { dev: '/journal/dev/api', prod: '/journal/prod/v2/api' };

const TEMPLATES: AlertTemplate[] = [
  {
    kind: 'audit-object-touched', severity: 'p1', source: 'aws-event', environment: 'account',
    summary: () => 'A retention call on an audit object was denied (account-wide rule).',
    payload: n => ({ eventName: 'PutObjectRetention', principalArn: 'arn:aws:iam::419445948432:role/journal-dev-v2-api-task', bucket: 'journal-ai-audit-copy-dev', key: `2026-09/run-${uuidLike('run' + n).slice(0, 8)}/ai_outputs.jsonl`, errorCode: 'AccessDenied' }),
    evidence: n => [{ kind: 'cloudtrail-event', eventId: uuidLike('ct' + n), eventName: 'PutObjectRetention' }],
  },
  {
    kind: 'export-task-failed', severity: 'p2', source: 'ecs-task', environment: 'dev',
    summary: () => 'The nightly AI audit export task stopped with exit code 1 on dev.',
    payload: n => ({ taskFamily: 'journal-dev-ai-audit-export', exitCode: 1, stoppedReason: 'Essential container in task exited', taskId: uuidLike('task' + n).slice(0, 12) }),
    evidence: n => [{ kind: 'ecs-task', taskArn: `arn:aws:ecs:us-west-2:419445948432:task/journal-dev/${uuidLike('task' + n).replace(/-/g, '')}`, exitCode: 1, logStream: `ai-audit-export/${uuidLike('ls' + n).slice(0, 8)}` }],
  },
  {
    kind: 'export-completed-absent', severity: 'p2', source: 'cloudwatch-alarm', environment: 'dev',
    summary: () => 'No audit-export.completed marker for 30 hours on dev.',
    payload: () => ({ alarmName: 'journal-dev-ai-audit-export-completed-absent', newState: 'ALARM', reason: 'Threshold Crossed: no datapoints for 30 hours' }),
    evidence: () => [{ kind: 'alarm', alarmArn: 'arn:aws:cloudwatch:us-west-2:419445948432:alarm:journal-dev-ai-audit-export-completed-absent' }],
  },
  {
    kind: 'marker:plan-my-day.assist-blocked', severity: 'p2', source: 'cloudwatch-alarm', environment: 'dev',
    summary: n => `AI Assist blocks reached ${5 + (n % 4)} in one hour on dev (threshold 5).`,
    payload: n => ({ alarmName: 'journal-dev-compliance-assist-blocked', metric: 'plan-my-day.assist-blocked', count: 5 + (n % 4), threshold: 5, windowMinutes: 60 }),
    evidence: n => [{ kind: 'logs-insights', logGroup: LOG_GROUP.dev, query: 'fields @timestamp | filter @message like "plan-my-day.assist-blocked" | sort @timestamp desc', from: iso(NOW - (n + 1) * 7 * HOUR), to: iso(NOW - n * 7 * HOUR) }],
  },
  {
    kind: 'marker:plan-my-day.recap-withheld', severity: 'p2', source: 'cloudwatch-alarm', environment: 'dev',
    summary: n => `Withheld recaps reached ${3 + (n % 3)} in 24 hours on dev (threshold 3).`,
    payload: n => ({ alarmName: 'journal-dev-compliance-recap-withheld', metric: 'plan-my-day.recap-withheld', count: 3 + (n % 3), threshold: 3, windowMinutes: 1440 }),
    evidence: n => [{ kind: 'logs-insights', logGroup: '/journal/dev/api-worker', query: 'fields @timestamp | filter @message like "plan-my-day.recap-withheld" | sort @timestamp desc', from: iso(NOW - (n + 2) * DAY), to: iso(NOW - (n + 1) * DAY) }],
  },
  {
    kind: 'marker:plan-my-day.assist-role-leak', severity: 'p3', source: 'cloudwatch-alarm', environment: 'dev',
    summary: n => `Role-label leaks reached ${3 + (n % 2)} in 24 hours on dev (threshold 3).`,
    payload: n => ({ alarmName: 'journal-dev-compliance-assist-role-leak', metric: 'plan-my-day.assist-role-leak', count: 3 + (n % 2), threshold: 3, windowMinutes: 1440 }),
    evidence: n => [{ kind: 'logs-insights', logGroup: LOG_GROUP.dev, query: 'fields @timestamp | filter @message like "plan-my-day.assist-role-leak" | sort @timestamp desc', from: iso(NOW - (n + 2) * DAY), to: iso(NOW - (n + 1) * DAY) }],
  },
  {
    kind: 'marker:problem-response', severity: 'p3', source: 'cloudwatch-alarm', environment: 'dev',
    summary: n => `AI Assist server faults reached ${3 + (n % 5)} in one hour on dev (threshold 3).`,
    payload: n => ({ alarmName: 'journal-dev-compliance-assist-problem-response', metric: 'problem-response', route: '/assist', count: 3 + (n % 5), threshold: 3, windowMinutes: 60 }),
    evidence: n => [{ kind: 'logs-insights', logGroup: LOG_GROUP.dev, query: 'fields @timestamp | filter @message like "problem-response" and @message like "/assist" | sort @timestamp desc', from: iso(NOW - (n + 1) * 5 * HOUR), to: iso(NOW - n * 5 * HOUR) }],
  },
  {
    kind: 'marker:me.export', severity: 'p3', source: 'cloudwatch-alarm', environment: 'dev',
    summary: n => `Data exports reached ${10 + (n % 6)} in one hour on dev (threshold 10).`,
    payload: n => ({ alarmName: 'journal-dev-compliance-me-export', metric: 'me.export', count: 10 + (n % 6), threshold: 10, windowMinutes: 60 }),
    evidence: n => [{ kind: 'logs-insights', logGroup: LOG_GROUP.dev, query: 'fields @timestamp | filter @message like "me.export" | sort @timestamp desc', from: iso(NOW - (n + 1) * 9 * HOUR), to: iso(NOW - n * 9 * HOUR) }],
  },
];

/**
 * A derived alert as the backend's evaluator writes it: the summary template over the signal's fixed title, a
 * FLAT payload (signal, count, windowDays, the signal's own counts, evidenceIds, idsTruncated) and the evidence as
 * the user-risk link plus one table-rows item per view the rows came from.
 */
function derivedTemplate(user: RiskUser, signal: UserSignalStatus): AlertTemplate {
  const severity = signal.severity;
  const windowDays = signal.id === 'S1' ? MOCK_CRITERIA.s1.windowDays : SCORING.windowDays;
  const rows = user.timeline.filter(t => t.signalId === signal.id);
  const byView = new Map<string, string[]>();
  for (const t of rows) {
    const view = `v2_ai_audit_${t.source}`;
    byView.set(view, [...(byView.get(view) ?? []), t.rowId]);
  }
  const facts: AlertPayload = { ...signal.facts };
  return {
    kind: `signal:${signal.id}`, severity, source: 'derived', environment: 'dev',
    summary: () => `Signal ${signal.id} (${SIGNAL_TITLE[signal.id] ?? signal.id}) crossed its threshold for one user on dev: ${signal.count} in ${windowDays} days.`,
    payload: () => ({ signal: signal.id, count: signal.count, windowDays, ...facts, evidenceIds: rows.length, idsTruncated: false }),
    evidence: () => [
      { kind: 'user-risk', userId: user.userId },
      ...[...byView.entries()].filter(([view]) => view !== 'v2_ai_audit_users').map(([table, ids]) => ({ kind: 'table-rows' as const, table, ids })),
    ],
  };
}

function buildSeedAlerts(): SeedAlert[] {
  const rng = mulberry32(0xC0A11);
  const out: SeedAlert[] = [];
  const actors = ['admin:liron', 'admin:ops.reviewer'];

  const make = (t: AlertTemplate, n: number, ageMs: number, subjectUserId: string | null): SeedAlert => {
    const id = uuidLike(`alert-${t.kind}-${n}-${subjectUserId ?? ''}`);
    const occurred = NOW - ageMs;
    const received = occurred + Math.floor(rng() * 90_000);
    const seedEvents: ComplianceAlertEvent[] = [];
    // Older alerts tend to have been handled: acknowledged, some resolved with a note.
    const ageDays = ageMs / DAY;
    if (ageDays > 2 && rng() < 0.75) {
      const actor = actors[Math.floor(rng() * actors.length)]!;
      const ackAt = received + Math.floor(rng() * 8 * HOUR);
      seedEvents.push({ id: uuidLike(id + 'ack'), at: iso(ackAt), actor, action: 'acknowledged', note: null });
      const supervisorNeeded = requiresSupervisorToResolve(t.kind, t.severity);
      if (ageDays > 5 && rng() < 0.7) {
        seedEvents.push({
          id: uuidLike(id + 'res'), at: iso(ackAt + Math.floor(rng() * 2 * DAY)),
          actor: supervisorNeeded ? 'admin:liron' : actor, action: 'resolved',
          note: supervisorNeeded ? 'Reviewed each request; dispositions recorded; no output a customer saw carried advice.' : 'Checked the source; no follow-up needed.',
        });
      }
    }
    const raw = t.source === 'derived' || t.source === 'manual'
      ? null
      : { sha256: fakeSha256(id + 'raw'), body: JSON.stringify({ source: t.source, detail: t.payload(n) }, null, 2), truncated: false };
    const last = seedEvents[seedEvents.length - 1];
    return {
      row: {
        id, kind: t.kind, severity: t.severity, source: t.source, environment: t.environment,
        status: last ? (last.action === 'resolved' ? 'resolved' : 'acknowledged') : 'new',
        occurredAt: iso(occurred), receivedAt: iso(received), subjectUserId,
        summary: t.summary(n), lastActor: last?.actor ?? null, lastEventAt: last?.at ?? null,
        resolveRequiresSupervisor: requiresSupervisorToResolve(t.kind, t.severity),
      },
      fingerprint: t.source === 'derived' ? `${t.kind}:${subjectUserId}:${ymd(occurred)}` : null,
      snsMessageId: t.source === 'derived' ? null : uuidLike(id + 'sns'),
      payload: t.payload(n), raw, evidence: t.evidence(n), seedEvents,
      createdBy: t.source === 'derived' ? 'evaluator' : 'poller',
    };
  };

  // AWS-fed and alarm alerts, spread over the last 45 days; the P1 is recent and still open.
  TEMPLATES.forEach((t, ti) => {
    const count = t.severity === 'p1' ? 1 : 2 + (ti % 3);
    for (let n = 0; n < count; n++) {
      const age = t.severity === 'p1' ? 6 * HOUR : Math.floor(rng() * 45 * DAY) + HOUR;
      out.push(make(t, n, age, null));
    }
  });
  // Derived alerts: one per lit p1/p2 signal of the top users, at the signal's last occurrence.
  for (const u of riskUsers().slice(0, 12)) {
    for (const s of u.signals) {
      // The evaluator raises one alert per lit signal and trader (S20 is environment-wide, never about one trader).
      if (s.state !== 'lit' || s.id === 'S20' || !s.lastAt) continue;
      out.push(make(derivedTemplate(u, s), 0, NOW - Date.parse(s.lastAt), u.userId));
    }
  }
  return out.sort((a, b) => Date.parse(b.row.occurredAt) - Date.parse(a.row.occurredAt));
}

let _seedAlerts: SeedAlert[] | null = null;
function seedAlerts(): SeedAlert[] {
  if (!_seedAlerts) _seedAlerts = buildSeedAlerts();
  return _seedAlerts;
}

/** Session state: events appended in this session, and alerts raised by hand (a "Mark reviewed" record is one). */
const sessionEvents = new Map<string, ComplianceAlertEvent[]>();
const manualAlerts: SeedAlert[] = [];

const MOCK_ACTOR = 'admin:demo';

function allAlerts(): SeedAlert[] {
  return [...manualAlerts, ...seedAlerts()];
}

function eventsOf(a: SeedAlert): ComplianceAlertEvent[] {
  return [...a.seedEvents, ...(sessionEvents.get(a.row.id) ?? [])];
}

function currentRow(a: SeedAlert): ComplianceAlertRow {
  const events = eventsOf(a);
  const last = events[events.length - 1];
  return { ...a.row, status: deriveStatus(events), lastActor: last?.actor ?? null, lastEventAt: last?.at ?? null };
}

/** Status is the latest STATUS-changing event; a note never changes it; `reopened` returns to acknowledged. */
function deriveStatus(events: ComplianceAlertEvent[]): AlertStatus {
  let status: AlertStatus = 'new';
  for (const e of events) {
    if (e.action === 'acknowledged') status = 'acknowledged';
    else if (e.action === 'resolved') status = 'resolved';
    else if (e.action === 'reopened') status = 'acknowledged';
  }
  return status;
}

function toDetail(a: SeedAlert): ComplianceAlertDetail {
  return {
    ...currentRow(a),
    fingerprint: a.fingerprint, snsMessageId: a.snsMessageId, payload: a.payload, raw: a.raw,
    evidence: a.evidence, events: eventsOf(a), createdBy: a.createdBy,
  };
}

function findAlert(id: string): SeedAlert {
  const a = allAlerts().find(x => x.row.id === id);
  if (!a) throw new Error(`Unknown alert ${id}`);
  return a;
}

function appendEvent(id: string, action: ComplianceAlertEvent['action'], note: string | null): ComplianceAlertDetail {
  const a = findAlert(id);
  const list = sessionEvents.get(id) ?? [];
  list.push({ id: uuidLike(`${id}-${action}-${list.length}-${NOW}`), at: iso(Date.now()), actor: MOCK_ACTOR, action, note });
  sessionEvents.set(id, list);
  return toDetail(a);
}

export function mockOpenP1(): number {
  return allAlerts().map(currentRow).filter(r => r.severity === 'p1' && r.status !== 'resolved').length;
}

export function mockComplianceAlerts(params: ComplianceAlertsParams): ComplianceAlertsPage {
  const rows = allAlerts().map(currentRow);
  const base = rows.filter(r =>
    (!params.severity || r.severity === params.severity)
    && (!params.kind || r.kind === params.kind)
    && (!params.group || kindGroup(r.kind) === params.group)
    && (!params.environment || r.environment === params.environment)
    && (!params.from || r.occurredAt.slice(0, 10) >= params.from)
    && (!params.to || r.occurredAt.slice(0, 10) <= params.to));
  const counts: Record<AlertStatus, number> = { new: 0, acknowledged: 0, resolved: 0 };
  for (const r of base) counts[r.status]++;
  const filtered = params.status === 'open'
    ? base.filter(r => r.status !== 'resolved')
    : params.status ? base.filter(r => r.status === params.status) : base;
  filtered.sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
  const start = (params.page - 1) * params.pageSize;
  return {
    items: filtered.slice(start, start + params.pageSize),
    total: filtered.length, page: params.page, pageSize: params.pageSize, counts, openP1: mockOpenP1(),
  };
}

export function mockComplianceAlert(id: string): ComplianceAlertDetail {
  return toDetail(findAlert(id));
}

export function mockAcknowledgeAlert(id: string): ComplianceAlertDetail { return appendEvent(id, 'acknowledged', null); }
export function mockResolveAlert(id: string, note: string): ComplianceAlertDetail { return appendEvent(id, 'resolved', note); }
export function mockReopenAlert(id: string, note: string): ComplianceAlertDetail { return appendEvent(id, 'reopened', note); }
export function mockNoteAlert(id: string, note: string): ComplianceAlertDetail { return appendEvent(id, 'noted', note); }

/** The backend's templates for a manual alert and the escalation: the summary over closed words and counts, a flat payload of counts. */
export function mockCreateAlert(body: ComplianceCreateAlertRequest): ComplianceAlertDetail {
  const n = manualAlerts.length;
  const id = uuidLike(`manual-${n}-${NOW}`);
  const now = iso(Date.now());
  const rowIds = body.evidence.reduce((sum, e) => sum + (e.kind === 'table-rows' ? e.ids.length : 0), 0);
  const report = body.evidence.find((e): e is Extract<AlertEvidence, { kind: 'report' }> => e.kind === 'report');
  const summary = body.kind === 'audit-escalation'
    ? `Monthly AI audit escalation for ${report?.period ?? 'the period'} recorded on ${body.environment}, with ${rowIds} row ${rowIds === 1 ? 'id' : 'ids'} as evidence.`
    : `Manual ${body.severity.toUpperCase()} alert raised by an admin on ${body.environment}${body.subjectUserId ? ' about one user' : ''}.`;
  const payload: AlertPayload = { raisedBy: MOCK_ACTOR, evidenceItems: body.evidence.length, rowIds };
  if (body.kind === 'audit-escalation' && report) { payload.period = report.period; payload.reportSha256 = report.reportSha256; }
  const a: SeedAlert = {
    row: {
      id, kind: body.kind, severity: body.severity, source: 'manual', environment: body.environment, status: 'new',
      occurredAt: now, receivedAt: now, subjectUserId: body.subjectUserId, summary, lastActor: null, lastEventAt: null,
      resolveRequiresSupervisor: requiresSupervisorToResolve(body.kind, body.severity),
    },
    fingerprint: null, snsMessageId: null, payload,
    raw: null, evidence: body.evidence,
    seedEvents: [{ id: uuidLike(id + 'note0'), at: now, actor: MOCK_ACTOR, action: 'noted', note: body.note }],
    createdBy: MOCK_ACTOR,
  };
  manualAlerts.unshift(a);
  return toDetail(a);
}

// ─── Overview ──────────────────────────────────────────────────────────────────────────────

function windowStats(rng: () => number, surface: ComplianceSurface, days: number, daily: SurfaceDailyPoint[]): SurfaceWindowStats {
  const slice = daily.slice(-days);
  const rows = slice.reduce((n, d) => n + d.rows, 0);
  const finalBlocks = slice.reduce((n, d) => n + d.finalBlocks, 0);
  const checked = surface !== 'backtest-chat' && surface !== 'backtest-parse';
  const firstAnswersEnforce = checked ? Math.round(rows * 0.82) : 0;
  const firstAttemptBlocks = checked ? finalBlocks + Math.round(rows * (0.02 + rng() * 0.03)) : 0;
  const flags = checked ? Math.round(rows * (0.05 + rng() * 0.08)) : 0;
  const rate = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 10000) / 10000 : null);
  const isRecap = RECAP_SURFACES.includes(surface);
  return {
    rows, users: Math.max(1, Math.round(rows / (3 + rng() * 4))), firstAnswersEnforce, firstAttemptBlocks, finalBlocks,
    firstAttemptBlockRate: checked ? rate(firstAttemptBlocks, firstAnswersEnforce) : null,
    finalBlockRate: checked ? rate(finalBlocks, firstAnswersEnforce) : null,
    withheldRate: isRecap ? rate(finalBlocks, firstAnswersEnforce) : null,
    flags, flagsPer100Rows: checked ? rate(flags * 100, rows) : null,
  };
}

function buildSurfaces(): SurfaceStats[] {
  return COMPLIANCE_SURFACES.map(surface => {
    const rng = mulberry32(hashString('surface:' + surface));
    const level = surface === 'day-assist-plan' ? 28 : surface === 'day-assist-review' ? 18 : surface === 'day-recap' ? 22 : surface === 'day-recap-email' ? 9 : surface === 'day-write-up' ? 4 : surface === 'backtest-parse' ? 6 : 14;
    const checked = surface !== 'backtest-chat' && surface !== 'backtest-parse';
    const daily: SurfaceDailyPoint[] = [];
    for (let i = 29; i >= 0; i--) {
      const dow = new Date(NOW - i * DAY).getUTCDay();
      const weekend = dow === 0 || dow === 6;
      const rows = Math.max(0, Math.round(level * (weekend ? 0.3 : 1) * (0.6 + rng() * 0.8)));
      const finalBlocks = checked ? (rng() < 0.35 ? Math.round(rows * 0.04 * rng()) : 0) : 0;
      daily.push({ date: ymd(NOW - i * DAY), rows, finalBlocks });
    }
    const checkerMode: CheckerMode | null = surface === 'backtest-chat' || surface === 'backtest-parse' ? null : 'enforce';
    return { surface, checkerMode, last7d: windowStats(rng, surface, 7, daily), last30d: windowStats(rng, surface, 30, daily), daily };
  });
}

function buildRules(): RuleStats[] {
  const rng = mulberry32(0x5EED);
  const rowsChecked30d = 1840;
  return CHECKER_RULES.map(r => {
    const rowsHit = Math.round(rng() * (r.severity === 1 ? 6 : 40));
    const hits = rowsHit + Math.round(rng() * rowsHit * 0.4);
    const baseline = rng() < 0.85 ? Math.round(rng() * 0.03 * 10000) / 10000 : null;
    const rate = rowsHit / rowsChecked30d;
    return {
      ruleId: r.ruleId, severity: r.severity, hits30d: hits, rowsHit30d: rowsHit, rowsChecked30d,
      baselineRowsHitRate: baseline,
      doubled: baseline !== null && rowsHit >= 3 && rate > 2 * baseline,
    };
  });
}

export function mockComplianceOverview(params: ComplianceOverviewParams = {}): ComplianceOverview {
  const rows = allAlerts().map(currentRow);
  const awsFed = rows.filter(r => r.source === 'aws-event' || r.source === 'cloudwatch-alarm' || r.source === 'ecs-task');
  const lastMessageAt = awsFed.length ? awsFed.map(r => r.receivedAt).sort().at(-1)! : null;
  const open = rows.filter(r => r.status !== 'resolved');
  const users = riskUsers();
  const lastExport = NOW - 9 * HOUR;
  return {
    generatedAt: iso(Date.now()),
    environment: 'dev',
    available: true,
    header: {
      openP1: open.filter(r => r.severity === 'p1').length,
      openP2: open.filter(r => r.severity === 'p2').length,
      usersAboveThreshold: users.filter(u => u.score >= SCORING.reviewScore).length,
      requestsAwaitingDisposition: users.reduce((n, u) => n + pendingOf(u.timeline).length, 0),
      lastExportCompletedAt: iso(lastExport),
      daysSinceLastExport: Math.floor((NOW - lastExport) / DAY),
      reviewScoreThreshold: SCORING.reviewScore,
    },
    surfaces: buildSurfaces(),
    rules: buildRules(),
    checks: {
      darkSurfaceRows: 0, disclaimerVersion: MOCK_DISCLAIMER_VERSION, acceptancesInWindow: 41, provenanceGaps: 0,
      rulesetVersion: '2026-09-24.2', checkerMode: 'enforce', deployedSha: '316a585',
      evaluatorEnabled: true, lastEvaluationAt: iso(NOW - 25 * 60_000),
      alertChannel: { configured: false, lastMessageAt, lastPollAt: null }, auditExportEnabled: false, darkSurfaces: [],
    },
    window: overviewWindow(params.period),
    recentAlerts: rows.sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt)).slice(0, 10),
  };
}

/** The window the 30-day figures cover (B7). The mock does not recompute its figures for a month; it says which window was asked for. */
function overviewWindow(period: string | undefined): OverviewWindow {
  if (period && /^\d{4}-\d{2}$/.test(period)) {
    const [y, m] = period.split('-').map(Number) as [number, number];
    const from = Date.UTC(y, m - 1, 1);
    return { kind: 'month', period, from: iso(from), to: iso(Math.min(NOW, Date.UTC(y, m, 1))) };
  }
  return { kind: 'rolling-30d', period: null, from: iso(NOW - (SCORING.windowDays - 1) * DAY), to: iso(NOW) };
}

// ─── Users ─────────────────────────────────────────────────────────────────────────────────

function openAlertsFor(userId: string): ComplianceAlertRow[] {
  return allAlerts().map(currentRow).filter(r => r.subjectUserId === userId && r.status !== 'resolved');
}

function toUserRow(u: RiskUser): ComplianceUserRow {
  const lit = u.signals.filter(s => s.state === 'lit');
  const review = lastReviewOf(u.userId);
  return {
    userId: u.userId, email: u.email, score: u.score,
    signals: lit.map(s => ({ id: s.id, severity: SIGNAL_SEVERITY[s.id], count: s.count })),
    openAlerts: openAlertsFor(u.userId).length,
    lastAiActivityAt: u.lastAiActivityAt,
    dispositionsPending: pendingOf(u.timeline).length,
    firmOrTestAccount: u.firmOrTestAccount,
    lastReviewedAt: review?.at ?? null,
    lastReviewedBy: review?.by ?? null,
    newestSignalAt: lit.map(s => s.lastAt).filter((t): t is string => t !== null).sort().at(-1) ?? null,
  };
}

/**
 * The requests awaiting a review decision (B5), from the mock's timeline: every first answer held back (the rewrite
 * is the row's final block, if any) and every Block flag on an answer that was shown. Ids and closed words only.
 */
function pendingOf(timeline: readonly RiskTimelineEvent[]): PendingRequest[] {
  const out: PendingRequest[] = [];
  for (const t of timeline) {
    if (t.type === 'first-attempt-block') {
      const final = timeline.find(f => f.type === 'final-block' && f.signalId === 'S1' && Math.abs(Date.parse(f.at) - Date.parse(t.at)) < 60_000);
      out.push({
        rowId: t.rowId, source: 'assist_turns', surface: t.surface, at: t.at, firstAttempt: 'held-back',
        rewriteId: uuidLike(`${t.rowId}-rewrite`), rewriteOutcome: final ? 'held-back' : 'shown',
      });
    } else if (t.type === 'flag' && t.ruleSeverity === 1 && t.checkerMode === 'shadow') {
      out.push({ rowId: t.rowId, source: 'assist_turns', surface: t.surface, at: t.at, firstAttempt: 'shown', rewriteId: null, rewriteOutcome: null });
    }
  }
  return out.sort((a, b) => Date.parse(b.at) - Date.parse(a.at)).slice(0, 200);
}

export function mockComplianceUsers(params: ComplianceUsersParams): ComplianceUsersPage {
  const all = riskUsers();
  const start = (params.page - 1) * params.pageSize;
  return {
    items: all.slice(start, start + params.pageSize).map(toUserRow),
    total: all.length, page: params.page, pageSize: params.pageSize, scoring: SCORING, available: true, criteria: MOCK_CRITERIA,
  };
}

/** The latest "Mark reviewed" record about the user (its time and its admin), as the backend reads it back. */
function lastReviewOf(userId: string): { at: string; by: string } | null {
  const records = allAlerts()
    .filter(a => a.row.kind === USER_REVIEWED_KIND && a.row.subjectUserId === userId)
    .sort((a, b) => Date.parse(b.row.receivedAt) - Date.parse(a.row.receivedAt));
  const latest = records[0];
  return latest ? { at: latest.row.receivedAt, by: latest.createdBy } : null;
}

export function mockComplianceUser(userId: string): ComplianceUserRisk | null {
  const known = mockUsers().find(u => u.userId === userId);
  if (!known) return null;
  const u = riskUsers().find(x => x.userId === userId) ?? {
    userId, email: known.email, firmOrTestAccount: false, score: 0, lastAiActivityAt: null,
    signals: fullSignals(PHASE2_SIGNALS.map(id => ({ id, severity: SIGNAL_SEVERITY[id], state: 'clear' as const, count: 0, points: 0, firstAt: null, lastAt: null, facts: {} }))),
    timeline: [], acceptances: [], spend: { windowCostUsd: '0', calls: 0, unchargedCalls: 0, days: [] },
  };
  const openAlerts = openAlertsFor(userId);
  const linked = u.timeline.map(t => {
    const alert = t.signalId ? openAlerts.find(a => a.kind === `signal:${t.signalId}`) : undefined;
    return alert ? { ...t, alertId: alert.id } : t;
  });
  // Every alert about the user in the window is on the timeline too (`alert`, the kind as its label), as the backend sends it.
  const alertRows: RiskTimelineEvent[] = allAlerts().map(currentRow)
    .filter(a => a.subjectUserId === userId && Date.parse(a.occurredAt) >= NOW - SCORING.windowDays * DAY)
    .map(a => ({
      at: a.occurredAt, type: 'alert', signalId: a.kind.startsWith('signal:') ? a.kind.slice('signal:'.length) as SignalId : null,
      source: 'compliance_alerts', rowId: a.id, surface: null, ruleId: null, ruleSeverity: null, checkerMode: null,
      amountUsd: null, label: a.kind, alertId: a.id,
    }));
  const timeline = [...linked, ...alertRows].sort((a, b) => Date.parse(b.at) - Date.parse(a.at)).slice(0, 200);
  const r = lastReviewOf(userId);
  return {
    userId, email: u.email, firmOrTestAccount: u.firmOrTestAccount, score: u.score, evaluatedAt: iso(NOW - 25 * 60_000),
    available: true, signals: u.signals, timeline, acceptances: u.acceptances, openAlerts,
    dispositionsPending: pendingOf(linked).length,
    lastAiActivityAt: u.lastAiActivityAt, lastReviewedAt: r?.at ?? null, lastReviewedBy: r?.by ?? null,
    currentDisclaimerVersion: MOCK_DISCLAIMER_VERSION, pendingRequests: pendingOf(linked),
    criteria: MOCK_CRITERIA, spend: u.spend,
  };
}

/**
 * "Mark reviewed", as the backend records it: ONE manual alert of kind `user-reviewed` (p3, about the user, the
 * user-risk link as evidence, a summary of counts, a flat payload) whose single event is `resolved` and carries the
 * admin's note. Nothing else changes: the user's own alerts are not resolved, acknowledged or noted.
 */
export function mockMarkReviewed(userId: string, note: string): void {
  const now = iso(Date.now());
  const id = uuidLike(`reviewed-${userId}-${manualAlerts.length}-${NOW}`);
  const lit = (riskUsers().find(u => u.userId === userId)?.signals ?? []).filter(s => s.state === 'lit').map(s => s.id);
  const open = openAlertsFor(userId).length;
  const a: SeedAlert = {
    row: {
      id, kind: USER_REVIEWED_KIND, severity: 'p3', source: 'manual', environment: 'dev', status: 'resolved',
      occurredAt: now, receivedAt: now, subjectUserId: userId,
      summary: `An admin marked one user reviewed on dev (${lit.length} lit ${lit.length === 1 ? 'signal' : 'signals'}, ${open} open ${open === 1 ? 'alert' : 'alerts'}).`,
      lastActor: MOCK_ACTOR, lastEventAt: now, resolveRequiresSupervisor: false,
    },
    fingerprint: null, snsMessageId: null,
    payload: { reviewedBy: MOCK_ACTOR, litSignals: lit.join(','), openAlerts: open },
    raw: null, evidence: [{ kind: 'user-risk', userId }],
    seedEvents: [{ id: uuidLike(id + 'resolved'), at: now, actor: MOCK_ACTOR, action: 'resolved', note }],
    createdBy: MOCK_ACTOR,
  };
  manualAlerts.unshift(a);
}

/** Test seam: forget every session mutation (events, manual alerts, "Mark reviewed" records). */
export function resetMockComplianceSession(): void {
  sessionEvents.clear();
  manualAlerts.length = 0;
}
